import { useMutation, useQueryClient } from '@tanstack/react-query';
import { strapiDeleteManagedUser } from '../../api/strapiUsers';
import { queryKeys } from '../queryKeys';

/** Bulk-delete managed users (sequential DELETE /api/users/:id). */
export function useBulkDeleteManagedUsersMutation(jwt: string | null) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ userIds }: { userIds: string[] }) => {
      if (!jwt) throw new Error('Not signed in');
      if (userIds.length === 0) throw new Error('Select at least one user.');

      const failures: { id: string; message: string }[] = [];
      for (const id of userIds) {
        try {
          await strapiDeleteManagedUser(jwt, id);
        } catch (err) {
          failures.push({
            id,
            message: err instanceof Error ? err.message : 'Unable to delete user.',
          });
        }
      }

      if (failures.length > 0) {
        await qc.invalidateQueries({ queryKey: [...queryKeys.strapi, 'users'] });
        const preview = failures
          .slice(0, 3)
          .map((f) => `${f.id}: ${f.message}`)
          .join('; ');
        const more = failures.length > 3 ? ` (+${failures.length - 3} more)` : '';
        throw new Error(
          `Deleted ${userIds.length - failures.length} of ${userIds.length} users. Failed: ${preview}${more}`,
        );
      }

      return { deletedCount: userIds.length };
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: [...queryKeys.strapi, 'users'] });
    },
  });
}
